import React, {ReactNode} from "react";
import '../../../styles/components/lesson/score/score-modal.scss';
import Modal from "../../common/Modal";
import {iconImages} from "../../../config/config";
import Button from "../../common/Button";
import LoadingAnimation2 from "../../loadingAnimation/LoadingAnimation2";
import Lesson from "../../../models/view/lessonPage/Lesson";
import Segment from "../../../models/view/lessonPage/Segment";
import {useAppSelector} from "../../../redux/hooks";
import {lessonPlayerStore} from "../../../redux/lesson/LessonPlayerStore";

interface IScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplay: () => void;
  onNextButtonClick: () => void;
  onNextLessonClick: () => void;
}

export default function ScoreModal({onClose, onReplay, onNextButtonClick, onNextLessonClick, isOpen}: IScoreModalProps) {
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const lesson = useAppSelector(lessonPlayerStore.getLesson);
  const scoreModalViewModel = useAppSelector(lessonPlayerStore.getScoreModalViewModel);

  if(!isOpen){ return null; }

  const scoreModalClassName = `score-modal`;

  if(!scoreModalViewModel) {
    return (
      <Modal additionalClassName={scoreModalClassName} midiCloseShortcutNote={`B`} onClose={onClose}>
        <div className={`score-modal-content`}>
          <LoadingAnimation2/>
        </div>
      </Modal>
    );
  }

  const {
    shouldDisplayStars,
    didLevelUp,
    starsAwarded,
    level,
    closeNotesPercent,
    missedNotesPercent,
    pointsAwarded,
    pointsUntilNextLevel,
    progressDirection,
    shouldDisplayAccuracy
  } = scoreModalViewModel;

  const shouldDisplayCongratsMessage = shouldDisplayStars || didLevelUp;

  const navigationButtonsElement = createNavigationButtonsElement(
    segment,
    lesson,
    onReplay,
    onNextButtonClick,
    onNextLessonClick,
    "Replay",
    shouldDisplayCongratsMessage,
  );

  const congratsMessageElement = shouldDisplayCongratsMessage
    ? createCongratsMessagingElement(level, starsAwarded, navigationButtonsElement, shouldDisplayStars, didLevelUp)
    : null;
  const withCongratsMessageClassName = shouldDisplayCongratsMessage ? `with-congrats-message` : ``;

  const accuracyAndPointsElement = createAccuracyAndPointsElement(missedNotesPercent,
    closeNotesPercent,
    progressDirection,
    pointsAwarded,
    pointsUntilNextLevel,
    shouldDisplayAccuracy,
    !shouldDisplayCongratsMessage ? navigationButtonsElement : null
  );

  return (
    <Modal additionalClassName={scoreModalClassName} midiCloseShortcutNote={`B`} onClose={onClose}>
      <div className={`score-modal-content ${withCongratsMessageClassName}`}>
        {congratsMessageElement}
        {accuracyAndPointsElement}
      </div>
    </Modal>
  );
}

function createAccuracyAndPointsElement(
  incorrectNotesThatArentClosePercent: number,
  closeNotesPercent: number,
  progressDirection: "up" | "down" | undefined,
  pointsEarned: number,
  pointsUntilNextLevel: number,
  shouldDisplayAccuracy: boolean,
  navigationButtons: ReactNode | undefined
){
  const pointsEarnedEl = createPointsEarnedElement(pointsEarned);
  const pointsUntilNextLevelEl = createPointsUntilNextLevelElement(pointsUntilNextLevel);
  const accuracyEl = createAccuracyScoreElement(incorrectNotesThatArentClosePercent, closeNotesPercent, progressDirection, shouldDisplayAccuracy);
  const withNavButtonsClass = navigationButtons ? `with-nav-buttons` : ``;
  return <div className={`accuracy-and-points ${withNavButtonsClass}`}>
    {pointsEarnedEl}
    {pointsUntilNextLevelEl}
    {accuracyEl}
    {navigationButtons}
  </div>
}

function createNavigationButtonsElement(
  segment: Segment | undefined,
  lesson: Lesson | undefined,
  primaryOnClick: ()=> void,
  onClickNextButton: (()=> void) | undefined,
  onClickNextLessonButton: ()=> void,
  primaryText: string,
  shouldReversePrimaryAndSecondaryStylingDueToPurpleBackground: boolean,
) {
  const button1ClassName = shouldReversePrimaryAndSecondaryStylingDueToPurpleBackground ? `secondary` : `primary`;
  const button2ClassName = shouldReversePrimaryAndSecondaryStylingDueToPurpleBackground ? `primary` : `secondary`;
  const nextButton = onClickNextButton
    ?
    <Button
      navigationNote={"D"}
      buttonType={button1ClassName}
      onClick={onClickNextButton}
      text={"Next"}
    />
    : null;

  const shouldShowNextLessonButton = canShowNextLessonButton(segment, lesson);

  const nextLessonButton = (shouldShowNextLessonButton
    ? <Button
      navigationNote={"D"}
      buttonType={button1ClassName}
      additionalClassName={'nextLessonButton'}
      onClick={onClickNextLessonButton}
      text={'Next Lesson'}>
      <div className={"nextLessonContent"}>
        <div className={"nextLessonInfo"}>
          <img className={"nextLessonImg"} src={lesson?.nextLesson?.coverArtUrl} alt={''} />
          <div className={"nextLessonLabels"}>
            <p className={"nextLessonTitle"}>{lesson?.nextLesson?.title}</p>
            <p className={"nextLessonSubtitle"}>{lesson?.nextLesson?.subTitle}</p>
          </div>
        </div>
      </div>
    </Button>
    : null
  );
  return <div className="navigation-buttons">
    <Button navigationNote={"C"} buttonType={button2ClassName} onClick={primaryOnClick} text={primaryText}/>
    {shouldShowNextLessonButton ? nextLessonButton : nextButton}
  </div>
}

function canShowNextLessonButton(segment: Segment | undefined, lesson: Lesson | undefined) {
  const nextLessonExists = lesson && lesson?.nextLesson;

  if (!nextLessonExists) {
    return false;
  }

  let isLastSegment = true;
  const lastSegmentIndex = (lesson?.segments ??[]).length -1;

  if (lastSegmentIndex !== -1) {
    isLastSegment = segment?.id === lesson?.segments[lastSegmentIndex].id.toString();
  }

  return isLastSegment;
}

function createPointsEarnedElement(pointsEarned: number,){
  return <div className="points-earned">
    <div>POINTS AWARDED</div>
    <div>{pointsEarned}</div>
  </div>
}

function createPointsUntilNextLevelElement(pointsUntilNextLevel: number){
  return <div className={`points-until-next-level`}>
    <div>POINTS UNTIL NEXT LEVEL</div>
    <div>{pointsUntilNextLevel}</div>
  </div>
}

function createCongratsMessagingElement(newLevel: number, starsAwarded: number, navigationButtonsElement: ReactNode, shouldDisplayStars:boolean, didLevelUp: boolean){
  const levelUpOrStarsEarnedMessageEl = createLevelUpOrStarsEarnedMessageElement(newLevel, starsAwarded, shouldDisplayStars, didLevelUp);
  return <div className={`congrats-messaging`}>
    {levelUpOrStarsEarnedMessageEl}
    {navigationButtonsElement}
  </div>
}

function createLevelUpOrStarsEarnedMessageElement(newLevel: number, starsAwarded: number, shouldDisplayStars:boolean, didLevelUp: boolean){
  if(shouldDisplayStars && didLevelUp){
    return <div className={`congrats-new-level-and-stars`}>
      <div>CONGRATULATIONS!</div>
      <div>You reached Level {newLevel}!</div>
      {createStarsElement(starsAwarded, ``, shouldDisplayStars)}
    </div>
  } else if(didLevelUp){
    return <div className="congrats-new-level">
      <div>NEW LEVEL</div>
      <div>Nice! You reached Level {newLevel}.</div>
    </div>;
  }else if(shouldDisplayStars){
    return <div className="congrats-stars-earned">
      <div>YOUR STARS</div>
      <div>{determineCongratsMessageBasedOnStars(starsAwarded)}</div>
      {createStarsElement(starsAwarded, ``, shouldDisplayStars)}
    </div>;
  }
}

function determineCongratsMessageBasedOnStars(starsAwarded: number){
  switch(starsAwarded){
    case 1: return "You earned one star.";
    case 2: return "Good job! You earned two stars.";
    case 3: return "Hooray! You earned three stars.";
  }
}

function createStarsElement(starsAwarded: number, levelAndStarsPresentClassName: string, shouldDisplayStars: boolean){
  if(!shouldDisplayStars){ return null; }
  return <div className={`stars ${levelAndStarsPresentClassName}`}>
    {createStar(starsAwarded > 0)}
    {createStar(starsAwarded > 1)}
    {createStar(starsAwarded > 2)}
  </div>;
}

function createStar(isAwarded: boolean){
  return  <img className="star" src={isAwarded ?iconImages.starWon : iconImages.starLost} alt=''/>;
}

function createAccuracyScoreElement(incorrectNotesThatArentClosePercent: number, closeNotesPercent: number, progressDirection: "up" | "down" | undefined, shouldDisplayAccuracy: boolean){
  if(!shouldDisplayAccuracy){ return null;}
  const correctNotesPercentage = 100 - incorrectNotesThatArentClosePercent - closeNotesPercent;
  const correctNotesPercent = `${correctNotesPercentage}`;
  const cssVariables = {
    "--incorrect-notes-percent": `${incorrectNotesThatArentClosePercent}%`,
    "--close-notes-percent": `${closeNotesPercent}%`
  } as React.CSSProperties;

  return <div className="accuracy-score">
    <div className="accuracy-label">ACCURACY</div>
    <div className="pie-chart" style={cssVariables}>
      <div className="outer">
        <div className="chart">
          <div className="inner">
            {/*<div className="arrow">{progressArrowElement}</div>*/}
          </div>
        </div>
      </div>
    </div>
    <div className={`accuracy-percent ${correctNotesPercentage >= 80 ? 'green': ''} ${correctNotesPercentage === 100 ? 'perfect' : ''}`}>
      <div className={`number`}>{correctNotesPercent}<div className="percent-sign">%</div></div>
    </div>
  </div>;
}
